// Iter60 Task 2 — Admin notification card for new partner applications.
// Surfaces unreviewed partner applications front-and-center on the admin
// dashboard so a new partner submission can never be missed.
//
// Reads /api/admin/applications?role=partner. Shows:
//   • Partner name
//   • Business name
//   • Selected business category (label, not raw enum)
//   • Application status (new / reviewing / approved / denied)
//   • Date/time submitted
//
// Clickable rows open the full applications inbox for review.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Store, Package, Sparkles, Building2, AlertTriangle, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const CATEGORY_LABEL = {
  product_brand: "Product Brand",
  store:         "Store",
  dispensary:    "Dispensary",
  glassblower:   "Glassblower / Glass Shop",
  vape_store:    "Vape-Specific Store",
  artist:        "Artist",
  other:         "Other Business",
};
const CATEGORY_ICON = {
  product_brand: Package,
  store:         Store,
  dispensary:    Store,
  glassblower:   Sparkles,
  vape_store:    Store,
  artist:        Sparkles,
  other:         Building2,
};
const STATUS_CLS = {
  new:       "bg-amber-500/15 text-amber-300 border-amber-400/40",
  reviewing: "bg-sky-500/15 text-sky-300 border-sky-400/40",
  approved:  "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  denied:    "bg-rose-500/15 text-rose-300 border-rose-400/40",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminPartnerApplicationsCard({ limit = 6 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get("/admin/supplies/applications", {
          params: { role: "partner" },
        });
        if (cancelled) return;
        const items = (data?.items || []).filter(
          (r) => r.status !== "approved" && r.status !== "denied",
        );
        setRows(items.slice(0, limit));
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  const newCount = useMemo(
    () => rows.filter((r) => r.status === "new").length,
    [rows],
  );

  return (
    <section
      className="glass-panel p-5 border-amber-400/30"
      data-testid="admin-partner-applications"
    >
      <header className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Bell className="text-amber-300" size={16} />
          <h2 className="font-anton text-xl uppercase text-white">
            New Partner Applications
          </h2>
          {newCount > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[1.5rem] px-2 h-5 text-[10px] font-anton rounded-full bg-amber-400 text-black"
              data-testid="admin-applications-new-count"
            >
              {newCount}
            </span>
          )}
        </div>
        <Link
          to="/admin/applications"
          className="text-xs text-lifted-green hover:underline"
          data-testid="admin-applications-open"
        >
          Review all →
        </Link>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-lifted-dim" data-testid="admin-applications-loading">
          <Loader2 className="animate-spin" size={12} /> Loading…
        </div>
      )}
      {err && !loading && (
        <div className="flex items-center gap-2 text-xs text-rose-300" data-testid="admin-applications-error">
          <AlertTriangle size={12} /> {err}
        </div>
      )}
      {!loading && !err && rows.length === 0 && (
        <div className="text-xs text-lifted-dim" data-testid="admin-applications-empty">
          No pending partner applications. You&apos;ll see them here the moment one comes in.
        </div>
      )}

      <ul className="space-y-2" data-testid="admin-applications-list">
        {rows.map((r) => {
          const cat = r.business_category || (r.payload || {}).partner_type;
          const catLabel = CATEGORY_LABEL[cat] || (cat ? cat.replace(/_/g, " ") : "—");
          const Icon = CATEGORY_ICON[cat] || Building2;
          const status = r.status || "new";
          const businessName = r.business_name
            || (r.payload || {}).store_name
            || (r.payload || {}).brand_name
            || (r.payload || {}).company_name
            || "—";
          return (
            <li
              key={r.id}
              className="flex items-center gap-3 p-2 rounded-md bg-white/[0.02] hover:bg-white/[0.05] border border-white/5"
              data-testid={`admin-application-${r.id}`}
            >
              <div className="w-8 h-8 rounded-md bg-lifted-green/10 border border-lifted-green/30 grid place-items-center shrink-0">
                <Icon className="text-lifted-green" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-anton uppercase text-white text-sm truncate">
                    {r.name || "—"}
                  </span>
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.16em] font-bebas border ${STATUS_CLS[status] || STATUS_CLS.new}`}
                    data-testid={`admin-application-status-${r.id}`}
                  >
                    {status}
                  </span>
                </div>
                <div className="text-xs text-lifted-dim truncate flex flex-wrap gap-x-3">
                  <span data-testid={`admin-application-business-${r.id}`}>
                    {businessName}
                  </span>
                  <span className="text-lifted-green" data-testid={`admin-application-category-${r.id}`}>
                    {catLabel}
                  </span>
                </div>
                <div
                  className="text-[10px] text-lifted-dim font-mono mt-0.5"
                  data-testid={`admin-application-submitted-${r.id}`}
                >
                  {fmtDate(r.created_at)} · {r.email}
                </div>
              </div>
              <Link
                to={`/admin/applications?focus=${r.id}`}
                className="text-[10px] uppercase tracking-[0.18em] font-bebas px-2 py-1 rounded-md border border-white/15 hover:border-lifted-green/60 hover:text-lifted-green text-lifted-dim"
                data-testid={`admin-application-review-${r.id}`}
              >
                Review
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
